import type {
  Section,
  SectionOption,
} from "@components/NestedItemSelect/NestedItemSelect";
import type { SourceItem } from "../types";

// One row in the parent-line picker. A section's options are a flat list of
// service rows, each followed by its subservice rows. Ids are prefixed
// (`s-<id>` / `ss-<id>`) so services and subservices can't collide.
export interface LineOption extends SectionOption {
  kind: "service" | "subservice";
  serviceId: string; // own id for a service row, parent id for a subservice row
  hours: string;
  quantity: number;
}

export const serviceOptionId = (id: string) => `s-${id}`;
export const subserviceOptionId = (id: string) => `ss-${id}`;

const rawId = (o: LineOption) =>
  String(o.id).replace(o.kind === "service" ? /^s-/ : /^ss-/, "");

const normalizeSection = (
  prev: Section | undefined,
  next: Section
): Section => {
  const before = new Map(
    (prev?.options ?? []).map((o) => [String(o.id), o.selected])
  );
  const options = next.options.map((o) => ({ ...o })) as LineOption[];
  const wasSelected = (o: LineOption) => before.get(String(o.id)) ?? o.selected;

  // Services whose own row changed carry their subservices with them.
  const changedServices = new Map<string, boolean>();
  options.forEach((o) => {
    if (o.kind === "service" && wasSelected(o) !== o.selected) {
      changedServices.set(o.serviceId, o.selected);
    }
  });
  options.forEach((o) => {
    if (o.kind === "subservice" && changedServices.has(o.serviceId)) {
      o.selected = changedServices.get(o.serviceId) as boolean;
    }
  });

  // A subservice dropped from a selected service means the service is no
  // longer copied whole; keep the remaining subservices.
  const brokenServices = new Set<string>();
  options.forEach((o) => {
    if (
      o.kind === "subservice" &&
      !changedServices.has(o.serviceId) &&
      wasSelected(o) &&
      !o.selected
    ) {
      brokenServices.add(o.serviceId);
    }
  });
  options.forEach((o) => {
    if (o.kind === "service" && brokenServices.has(o.serviceId)) {
      o.selected = false;
    }
  });

  return {
    ...next,
    options,
    selected: options.some((o) => o.selected),
  };
};

// Applies the service/subservice selection rules to the sections that
// NestedItemSelect hands back, by comparing each option before and after.
export const normalize = (prev: Section[], next: Section[]): Section[] =>
  next.map((s) =>
    normalizeSection(
      prev.find((p) => p.id === s.id),
      s
    )
  );

// A selected service copies with all of its subservices, so it is sent alone;
// otherwise each selected subservice is sent (the API carries its service).
export const toSourceItems = (sections: Section[]): SourceItem[] => {
  const items: SourceItem[] = [];
  sections.forEach((section) => {
    const options = section.options as LineOption[];
    const selectedServices = new Set(
      options
        .filter((o) => o.kind === "service" && o.selected)
        .map((o) => o.serviceId)
    );
    options.forEach((o) => {
      if (!o.selected) return;
      if (o.kind === "service") {
        items.push({ type: "project-services", id: rawId(o) });
      } else if (!selectedServices.has(o.serviceId)) {
        items.push({ type: "project-subservices", id: rawId(o) });
      }
    });
  });
  return items;
};
